import { Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { ReactComponent as FillIcon } from 'assets/icons/drop.svg';
import { ReactComponent as MirrorIcon } from 'assets/icons/mirror_horizontal.svg';
import { useIconData } from 'hooks/useIcon';
import { ColorPicker } from 'modules/Editor/components/ColorPicker';
import { AltTextMenu } from 'modules/Editor/components/Toolbar/AltTextMenu/AltTextMenu';
import { ToolbarDivider } from 'modules/common/components/Toolbar/ToolbarDivider';
import { SliderPopover } from 'modules/common/components/ToolbarPopover';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { getElementForWidgetId } from 'utils/dom';
import { LinkMenu, ReplaceButton, useEditor, useWidget } from 'widgets/sdk';
import { InteractiveMenu } from 'widgets/sdk/Toolbar/InteractiveMenu';

import { DEFAULT_GRID_GAP_PX, MAX_GAP_PX } from './IconWidget.config';
import { getHeightOfGap } from './IconWidget.helpers';
import { IconWidgetData, IconWidgetType } from './IconWidget.types';
import { AdminIconWidgetToolbar } from './IconWidgetToolbar.admin';

export const IconWidgetToolbarMenu = (): ReactElement => {
  const { type = IconWidgetType.Single } = useWidget<IconWidgetData>();

  return type === IconWidgetType.Single ? <SingleIconToolbar /> : <GridIconToolbar />;
};

const SingleIconToolbar = () => {
  const { iconId, widgetId, iconMetadata, hyperlink } = useWidget<IconWidgetData>();
  const iconData = useIconData({ iconId, iconMetadata });

  const iconHasColorMenu = iconData && iconData.isMono;

  return (
    <Flex mr='8px' gap='8px' align='center'>
      <ReplaceButton />
      {iconHasColorMenu && <IconColorMenu />}
      <IconMirrorMenu />
      <Link />
      <ToolbarDivider />
      <AltTextMenu widgetId={widgetId} />
      <ToolbarDivider />
      <InteractiveMenu isDisabled={!!hyperlink} />
      <AdminIconWidgetToolbar />
    </Flex>
  );
};

const GridIconToolbar = () => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'iconToolbar.grid' });
  return (
    <Flex mr='8px' gap='8px' align='center'>
      <FillColorOne label={t('dataColor')} />
      <FillColorTwo label={t('nonDataColor')} />
      <ToolbarDivider />
      <GridGap />
      <AdminIconWidgetToolbar />
    </Flex>
  );
};

const GridGap = () => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'iconToolbar.grid' });
  const { updateWidget, widgetId, gridGapPx = DEFAULT_GRID_GAP_PX } = useWidget<IconWidgetData>();
  const { boundingBox } = useEditor();

  const onChangeGridGap = (newGridGapPx: number) => {
    if (newGridGapPx === gridGapPx) return;
    // if the widget is not rendered yet we can't measure the height
    if (!getElementForWidgetId(widgetId)) return;

    const newHeightPx = getHeightOfGap(widgetId, newGridGapPx);
    updateWidget({ gridGapPx: newGridGapPx, heightPx: newHeightPx });
    // widget dimensions have changed update the bounding box rect
    setTimeout(() => boundingBox.updateRect(), 0);
  };

  return (
    <SliderPopover
      value={gridGapPx}
      onChange={onChangeGridGap}
      max={MAX_GAP_PX}
      title={t('spacing.title')}
      buttonTitle={t('spacing.buttonTitle')}
    />
  );
};

const IconMirrorMenu = (): ReactElement | null => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'iconToolbar' });

  const { isMirrored, updateWidget } = useWidget<IconWidgetData>();
  const onUpdateIconMirror = () => updateWidget({ isMirrored: !isMirrored });

  if (typeof isMirrored !== 'boolean') return null;

  return (
    <Tooltip hasArrow placement='bottom' label={t('mirror.tooltip')} bg='black'>
      <IconButton
        onClick={onUpdateIconMirror}
        size='sm'
        aria-label={t('mirror.ariaLabel')}
        variant='icon-btn-toolbar-option'
        icon={<MirrorIcon />}
      />
    </Tooltip>
  );
};

const IconColorMenu = (): ReactElement => {
  const { updateWidget, shapeFill } = useWidget<IconWidgetData>();

  const onUpdateShapeFill = (newShapeFill: number) => updateWidget({ shapeFill: newShapeFill });
  const hasSecondaryColorFill = shapeFill > 0;

  return (
    <>
      <FillColorOne />
      {hasSecondaryColorFill && <FillColorTwo />}
      <FillPercentOption value={shapeFill} onChange={onUpdateShapeFill} />
    </>
  );
};

const FillColorOne = ({ label }: { label?: string }) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'iconToolbar' });

  const { updateWidget, shapeColorOne } = useWidget<IconWidgetData>();
  const onUpdateColorOne = (newShapeColorOne: string) => updateWidget({ shapeColorOne: newShapeColorOne });

  return (
    <ColorPicker
      color={shapeColorOne}
      label={label || t('fillColor.one')}
      onChange={onUpdateColorOne}
      showNoColorOption={false}
    />
  );
};

const FillColorTwo = ({ label }: { label?: string }) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'iconToolbar' });

  const { updateWidget, shapeColorTwo } = useWidget<IconWidgetData>();
  const onUpdateColorTwo = (newShapeColorTwo: string) => updateWidget({ shapeColorTwo: newShapeColorTwo });

  return (
    <ColorPicker
      color={shapeColorTwo}
      label={label || t('fillColor.two')}
      onChange={onUpdateColorTwo}
      showNoColorOption={false}
    />
  );
};

const FillPercentOption = ({ value, onChange }: { value: number; onChange: (f: number) => void }) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'iconToolbar' });

  return (
    <SliderPopover
      value={value}
      icon={<FillIcon />}
      label={t('fillPercent.label')}
      onChange={onChange}
      suffix={'%'}
      max={100}
      title={t('fillPercent.title')}
    />
  );
};

const Link = () => {
  const { hyperlink, updateWidget } = useWidget<IconWidgetData>();
  const isActive = !!hyperlink;

  const onChange = (newUrl: string | null) => {
    if (newUrl === null) return;
    if (newUrl === '') return updateWidget({ hyperlink: '' });

    // When a link is added to an image - set isDecorative to false so that it will be read by screen readers
    return updateWidget({ hyperlink: newUrl, isDecorative: false, interactions: null });
  };

  return <LinkMenu isActive={isActive} onChange={onChange} value={hyperlink} />;
};
